import { USER_ROLE } from '@trustero/trustero-api-web/lib/account/account_pb'
import {
  CAPABILITY,
  PERMISSION_RULE,
} from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import { useCallback, useEffect, useMemo } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { useAccount } from 'src/components/async/Account/account.hooks'
import { useUser } from 'src/components/async/Users'
import {
  AccessManagementAbsoluteRoutes,
  AiGrcQaAbsoluteRoutes,
  AuditBotAbsoluteRoutes,
  ControlsAbsoluteRoutes,
  DirectRoutes,
  EvidenceAbsoluteRoutes,
  ExamineAndTestAbsoluteRoutes,
  FrameworksAbsoluteRoutes,
  PoliciesAbsoluteRoutes,
  ReceptorsAbsoluteRoutes,
  RequestsAbsoluteRoutes,
  RisksAbsoluteRoutes,
  RoadmapAbsoluteRoutes,
  SchedulingAbsoluteRoutes,
  ScopeAbsoluteRoutes,
  SecurityQuestionnaireAbsoluteRoutes,
  ServicesAbsoluteRoutes,
  TrustPortalConfigurationAbsoluteRoutes,
  TrusteroIntelligenceAbsoluteRoutes,
  UserRolesAbsoluteRoutes,
  VendorsAbsoluteRoutes,
} from 'src/components/Reusable/RootPage/RootPage.constants'
import { getRoleConfig, PERMISSIONS, RoleConfig } from 'src/config/roleConfig'
import { useInAudit } from 'src/context/AuditContext'
import { useAuth, Permissions } from 'src/context/authContext'

export const checkPath = (routes: string[], path: string): boolean => {
  for (const route of routes) {
    const regex = new RegExp(`^/[a-z0-9:]+/${route}(?:/|$)`)
    if (regex.test(path)) {
      return true
    }
  }
  return false
}

export const isShowPageRoute = (path: string): boolean => {
  return /\/show\/[^/]+/.test(path)
}

/**
 * Every route a HAS_PAGE_ACCESS capability governs, so denying the capability hides the
 * nav entry and bounces the route. A capability only belongs here if it gates a page:
 * Chats, Playbooks, Departments and Content Versions have no page of their own - they
 * gate features inside other pages (the copilot panel, the copilot library, the
 * department pickers and the frameworks page), which their CAN_* capabilities cover.
 */
export const PAGE_ACCESS_CAPABILITY_ROUTES: Partial<
  Record<CAPABILITY, string[]>
> = {
  [CAPABILITY.HAS_PAGE_ACCESS_CONTROLS]: [ControlsAbsoluteRoutes.ROOT],
  [CAPABILITY.HAS_PAGE_ACCESS_POLICIES]: [PoliciesAbsoluteRoutes.ROOT],
  [CAPABILITY.HAS_PAGE_ACCESS_EVIDENCE]: [EvidenceAbsoluteRoutes.ROOT],
  [CAPABILITY.HAS_PAGE_ACCESS_RISKS]: [RisksAbsoluteRoutes.ROOT],
  [CAPABILITY.HAS_PAGE_ACCESS_SERVICES]: [ServicesAbsoluteRoutes.ROOT],
  [CAPABILITY.HAS_PAGE_ACCESS_RECEPTORS]: [ReceptorsAbsoluteRoutes.ROOT],
  [CAPABILITY.HAS_PAGE_ACCESS_FRAMEWORKS]: [FrameworksAbsoluteRoutes.ROOT],
  [CAPABILITY.HAS_PAGE_ACCESS_AUDITS]: [DirectRoutes.AUDITS],
  [CAPABILITY.HAS_PAGE_ACCESS_REQUESTS]: [RequestsAbsoluteRoutes.ROOT],
  [CAPABILITY.HAS_PAGE_ACCESS_REPORTS]: [DirectRoutes.REPORTS],
  [CAPABILITY.HAS_PAGE_ACCESS_ROADMAP]: [RoadmapAbsoluteRoutes.ROOT],
  [CAPABILITY.HAS_PAGE_ACCESS_DASHBOARDS]: [
    DirectRoutes.DASHBOARD,
    DirectRoutes.GRC_DASHBOARDS,
  ],
  [CAPABILITY.HAS_PAGE_ACCESS_USERS]: [DirectRoutes.USERS],
  [CAPABILITY.HAS_PAGE_ACCESS_SCOPE]: [ScopeAbsoluteRoutes.ROOT],
  [CAPABILITY.HAS_PAGE_ACCESS_AUDIT_SCANS]: [
    ExamineAndTestAbsoluteRoutes.ROOT,
    AuditBotAbsoluteRoutes.ROOT,
    ExamineAndTestAbsoluteRoutes.COMPLIANCE_ROOT,
  ],
  [CAPABILITY.HAS_PAGE_ACCESS_TRUST_PORTAL]: [
    TrustPortalConfigurationAbsoluteRoutes.ROOT,
    AccessManagementAbsoluteRoutes.ROOT,
  ],
  // security_questionnaire is the deprecated route the live ai_grc_qa page replaced;
  // both still resolve, so both have to be denied together.
  [CAPABILITY.HAS_PAGE_ACCESS_QUESTIONNAIRES]: [
    SecurityQuestionnaireAbsoluteRoutes.ROOT,
    AiGrcQaAbsoluteRoutes.ROOT,
  ],
  [CAPABILITY.HAS_PAGE_ACCESS_ACCOUNT_SETTINGS]: [
    DirectRoutes.ACCOUNT_SETTINGS,
  ],
  [CAPABILITY.HAS_PAGE_ACCESS_VENDORS]: [VendorsAbsoluteRoutes.ROOT],
  [CAPABILITY.HAS_PAGE_ACCESS_COMPANY_INFO]: [DirectRoutes.COMPANY_INFO],
  [CAPABILITY.HAS_PAGE_ACCESS_SCHEDULES]: [SchedulingAbsoluteRoutes.ROOT],
  [CAPABILITY.HAS_PAGE_ACCESS_TRUSTERO_INTELLIGENCE]: [
    TrusteroIntelligenceAbsoluteRoutes.ROOT,
  ],
  [CAPABILITY.HAS_PAGE_ACCESS_ACCOUNT_DETAILS]: [
    DirectRoutes.ACTIVITIES,
    DirectRoutes.USAGE,
    DirectRoutes.INDEXING,
  ],
  [CAPABILITY.HAS_PAGE_ACCESS_ACCESS_ROLES]: [UserRolesAbsoluteRoutes.ROOT],
  [CAPABILITY.HAS_PAGE_ACCESS_GRC_DOCUMENTS]: [DirectRoutes.MANAGE_DOCUMENTS],
}

// The order a "land on a page this role can open" flow walks: the dashboard first
// because it is the app's home, then everything else the registry knows about.
const PAGE_ACCESS_LANDING_ORDER: CAPABILITY[] = [
  CAPABILITY.HAS_PAGE_ACCESS_DASHBOARDS,
  ...(Object.keys(PAGE_ACCESS_CAPABILITY_ROUTES).map(Number) as CAPABILITY[]),
]

/**
 * The first page route the caller's role may open, or undefined when it may open none.
 * `isPageAccessAllowed` answers for a single HAS_PAGE_ACCESS capability.
 */
export const firstAllowedPageRoute = (
  isPageAccessAllowed: (capability: CAPABILITY) => boolean,
): string | undefined => {
  for (const capability of PAGE_ACCESS_LANDING_ORDER) {
    const routes = PAGE_ACCESS_CAPABILITY_ROUTES[capability]
    if (routes?.length && isPageAccessAllowed(capability)) {
      return routes[0]
    }
  }
  return undefined
}

const checkAuditPath = (allowedAudits: string[], path: string): boolean => {
  if (path.length === 0) {
    return false
  }

  for (const audit of allowedAudits) {
    if (path.includes(audit)) {
      return true
    }
  }
  return false
}

export const useIsSuperUser = (): boolean => {
  const { email, role } = useAuth()
  return email.includes('@trustero.com') && role === USER_ROLE.ADMIN
}

export const useValidateRouteAccess = (isAuthLoading: boolean): void => {
  const {
    routeConfig: { denyRoutes },
  } = useGetRoleAccessConfig()
  const location = useLocation()

  useEffect(() => {
    if (isAuthLoading) {
      return
    }
    const path = location.pathname

    if (checkPath(denyRoutes, path) && !isShowPageRoute(path)) {
      window.location.href = DirectRoutes.ACCESS_DENIED
    }
  }, [location.pathname, denyRoutes, isAuthLoading])
}

export const useRestrictAccessToAudit = (): void => {
  const { routeConfig } = useGetRoleAccessConfig()
  const { data } = useUser()
  const params = useParams()
  const location = useLocation()
  const { auditId } = useInAudit()

  useEffect(() => {
    const pageContext = params.pageContext
    if (!pageContext?.includes(data?.getAccountid() || '')) return
    if (!routeConfig.restrictToAuditView || !data || !pageContext) return

    const allowedAudits = data.getAuditIdsList()
    const path = location.pathname
    const onAllowedRoute = checkPath(routeConfig.explicitAllowRoutes, path)
    const inAllowedAuditView = checkAuditPath(allowedAudits, auditId || '')

    if (allowedAudits.length && onAllowedRoute && !inAllowedAuditView) {
      window.location.href = `../${data.getAccountid()}:${allowedAudits[0]}/${
        DirectRoutes.AUDITS
      }${location.search}`
    }
    if (!onAllowedRoute && !inAllowedAuditView) {
      if (allowedAudits.length > 0) {
        const targetPath = `../${data.getAccountid()}:${allowedAudits[0]}/${
          DirectRoutes.AUDITS
        }`
        if (location.pathname !== targetPath) {
          window.location.href = `${targetPath}${location.search}`
        }
      } else {
        if (location.pathname !== `${DirectRoutes.AUDITS}`) {
          window.location.href = `../${data.getAccountid()}/${
            DirectRoutes.AUDITS
          }${location.search}`
        }
      }
    }
  }, [
    routeConfig,
    data,
    params.pageContext,
    auditId,
    location.pathname,
    location.search,
  ])
}

export const useGetRoleAccessConfig = (): RoleConfig => {
  const authCtx = useAuth()
  const { data: user, isLoading } = useUser()

  const { data: account } = useAccount()

  return useMemo(() => {
    const baseConfig = getRoleConfig({
      userRole: authCtx.role,
      accountPurpose: account?.getPurpose(),
      hasQc: user?.getAllowQc(),
      isLoading,
    })

    if (!authCtx.permissions || authCtx.isAuthLoading) {
      return baseConfig
    }

    // Accounts not yet migrated to the new auth system answer getPermissions with no
    // capabilities at all; those keep the base route config rather than losing every page.
    const capabilities = authCtx.permissions.capabilities || {}
    if (Object.keys(capabilities).length === 0) {
      return baseConfig
    }

    const permissionDeniedRoutes: string[] = []

    for (const [capStr, routes] of Object.entries(
      PAGE_ACCESS_CAPABILITY_ROUTES,
    )) {
      const cap = Number(capStr) as CAPABILITY
      // A migrated account's capability payload is the page authority, and an absent
      // capability is denial rather than an invitation to fall back to the JWT's legacy
      // role. The rule is evaluated rather than compared to ALWAYS: a page capability
      // carrying IF_TRUSTERO_USER means staff reach the page and nobody else does, which
      // a straight ALWAYS test would read as "deny everyone".
      if (!checkCanPerformAction(cap, undefined, authCtx.permissions, user)) {
        permissionDeniedRoutes.push(...(routes || []))
      }
    }

    return {
      ...baseConfig,
      routeConfig: {
        ...baseConfig.routeConfig,
        // Do not blend the legacy role's route decisions into an access-role
        // account. The capability payload is complete for every page domain.
        denyRoutes: Array.from(new Set(permissionDeniedRoutes)),
        hideNavRoutes: Array.from(new Set(permissionDeniedRoutes)),
        hideNavSections: [],
        ccHideNavSections: [],
      },
    }
  }, [
    authCtx.role,
    authCtx.permissions,
    authCtx.isAuthLoading,
    account,
    user,
    isLoading,
  ])
}

export const useHasRequiredPermissions = (
  requiredPermissions: PERMISSIONS[],
): boolean => {
  const { permissionsConfig } = useGetRoleAccessConfig()

  for (const permission of requiredPermissions) {
    if (!permissionsConfig.includes(permission)) {
      return false
    }
  }

  return true
}

export const useHasEditPermissions = (): boolean => {
  return useHasRequiredPermissions([PERMISSIONS.EDIT])
}

export type ResourceWithAuthz = {
  ownerEmail?: string
  ownerId?: string
  assigneeEmailsList?: string[]
  assigneeEmails?: string[]
  labelIdsList?: string[]
  labelIds?: string[]
  getOwnerEmail?: () => string
  getOwnerId?: () => string
  getAssigneeEmailsList?: () => string[]
  getLabelIdsList?: () => string[]
}

export const checkCanPerformAction = <T extends ResourceWithAuthz>(
  capability: CAPABILITY,
  resource?: T,
  permissions?: Permissions | null,
  user?: {
    getEmail?: () => string
    getId?: () => string
  } | null,
): boolean => {
  if (!permissions) return false

  const capabilityInfo = permissions?.capabilities?.[capability]
  if (!capabilityInfo) return false

  // User Role ALWAYS and NEVER should never be affected by label permission.
  if (capabilityInfo.rule === PERMISSION_RULE.ALWAYS) {
    return true
  }
  if (capabilityInfo.rule === PERMISSION_RULE.NEVER) {
    return false
  }

  const getResourceLabels = (): string[] => {
    if (!resource) return []
    if (typeof resource.getLabelIdsList === 'function') {
      return resource.getLabelIdsList() || []
    }
    return resource.labelIdsList || resource.labelIds || []
  }

  const getResourceOwnerEmail = (): string | undefined => {
    if (!resource) return undefined
    if (typeof resource.getOwnerEmail === 'function') {
      return resource.getOwnerEmail()
    }
    return resource.ownerEmail
  }

  const getResourceOwnerId = (): string | undefined => {
    if (!resource) return undefined
    if (typeof resource.getOwnerId === 'function') {
      return resource.getOwnerId()
    }
    return resource.ownerId
  }

  const getResourceAssignees = (): string[] => {
    if (!resource) return []
    if (typeof resource.getAssigneeEmailsList === 'function') {
      return resource.getAssigneeEmailsList() || []
    }
    return resource.assigneeEmailsList || resource.assigneeEmails || []
  }

  const resourceLabelIds = getResourceLabels()
  // Empty ids are dropped so a blank entry in a restriction list cannot match a
  // resource whose label id is also blank. Mirrors rbac.toSet.
  const labelSet = (ids?: string[]) => new Set((ids || []).filter(Boolean))
  const userNoAccess = labelSet(permissions.noAccessLabelIds)
  const userReadOnly = labelSet(permissions.readOnlyLabelIds)
  const userFullAccess = labelSet(permissions.fullAccessLabelIds)

  const hasNoAccessLabel = resourceLabelIds.some((id) => userNoAccess.has(id))
  const hasFullAccessLabel = resourceLabelIds.some((id) =>
    userFullAccess.has(id),
  )
  const hasReadOnlyLabel = resourceLabelIds.some((id) => userReadOnly.has(id))

  // Keep this exactly aligned with rbac.IsViewCapability: a capability is a
  // read capability by its vocabulary, not by a list that must be maintained
  // every time the capability enum grows.
  const isViewCapability =
    capabilityInfo.capabilityName?.startsWith('CAN_VIEW_')

  // 2. IF_DENIED_PERMISSION ("Allow (Deny by Label)"): allowed by default, denied if the resource
  // has any label associated with the user/group.
  if (capabilityInfo.rule === PERMISSION_RULE.IF_DENIED_PERMISSION) {
    if (hasNoAccessLabel || hasReadOnlyLabel || hasFullAccessLabel) {
      return false
    }
    return true
  }

  // 3. If resource has any label restricted to No Access, deny.
  if (hasNoAccessLabel) {
    return false
  }

  // 4. If resource has a label restricted to Read Only (and not elevated by Full Access),
  // deny any mutating/editing action.
  if (!isViewCapability && hasReadOnlyLabel && !hasFullAccessLabel) {
    return false
  }

  // 5. Explicit label grants
  if (hasFullAccessLabel) {
    return true
  }

  if (isViewCapability && hasReadOnlyLabel) {
    return true
  }

  // 6. Fall back to the capability's own rule.
  //
  // IF_TRUSTERO_USER grants on Trustero staff. Mirrors rbac.IsTrusteroUser.
  if (capabilityInfo.rule === PERMISSION_RULE.IF_TRUSTERO_USER) {
    const email =
      typeof user?.getEmail === 'function' ? user.getEmail() : undefined
    return !!email && email.toLowerCase().endsWith('@trustero.com')
  }
  if (capabilityInfo.rule === PERMISSION_RULE.IF_OWNER) {
    const ownerEmail = getResourceOwnerEmail()
    const ownerId = getResourceOwnerId()
    const userEmail =
      typeof user?.getEmail === 'function' ? user.getEmail() : undefined
    const userId = typeof user?.getId === 'function' ? user.getId() : undefined
    return (
      (!!ownerEmail && ownerEmail === userEmail) ||
      (!!ownerId && ownerId === userId)
    )
  }
  if (capabilityInfo.rule === PERMISSION_RULE.IF_OWNER_OR_ASSIGNEE) {
    const ownerEmail = getResourceOwnerEmail()
    const ownerId = getResourceOwnerId()
    const userEmail =
      typeof user?.getEmail === 'function' ? user.getEmail() : undefined
    const userId = typeof user?.getId === 'function' ? user.getId() : undefined
    const isOwner =
      (!!ownerEmail && ownerEmail === userEmail) ||
      (!!ownerId && ownerId === userId)
    const isAssignee = !!userEmail && getResourceAssignees().includes(userEmail)
    return isOwner || isAssignee
  }
  if (capabilityInfo.rule === PERMISSION_RULE.IF_GRANTED_PERMISSION) {
    return false
  }

  return false
}

export const useCanPerformActionChecker = (): (<T extends ResourceWithAuthz>(
  capability: CAPABILITY,
  resource?: T,
) => boolean) => {
  const { permissions } = useAuth()
  const { data: user } = useUser()

  return useCallback(
    (capability: CAPABILITY, resource?: ResourceWithAuthz) =>
      checkCanPerformAction(capability, resource, permissions, user),
    [permissions, user],
  )
}

export const useCanPerformAction = <T extends ResourceWithAuthz>(
  capability: CAPABILITY,
  resource?: T,
): boolean => {
  const check = useCanPerformActionChecker()
  return check(capability, resource)
}

// Shared action controls use this during the role-config migration. A caller that
// knows the domain supplies its required capabilities; the component owns the
// consistent AND and resource-scope evaluation.
// The or-form of useHasRequiredCapabilities, for an action an rpc admits on any of
// several capabilities -- a relationship named from both sides, say. An empty or absent
// list is vacuously satisfied, matching rbac.HasAnyCapability, so passing none does not
// deny.
export const useHasAnyCapability = <T extends ResourceWithAuthz>(
  anyOfCapabilities?: CAPABILITY[],
  resource?: T,
): boolean => {
  const canPerformAction = useCanPerformActionChecker()
  if (!anyOfCapabilities?.length) return true
  return anyOfCapabilities.some((capability) =>
    canPerformAction(capability, resource),
  )
}

export const useHasRequiredCapabilities = <T extends ResourceWithAuthz>(
  requiredCapabilities?: CAPABILITY[],
  resource?: T,
): boolean => {
  const canPerformAction = useCanPerformActionChecker()
  return (
    requiredCapabilities?.every((capability) =>
      canPerformAction(capability, resource),
    ) ?? true
  )
}
